import ExcelJS from "exceljs";
import moment from "moment";
import React, { useState } from "react";
import { AiOutlineFileSearch, AiOutlineShop } from "react-icons/ai";
import { FaFileInvoice } from "react-icons/fa";
import { FiPhone, FiUser } from "react-icons/fi";
import { HiOutlineLocationMarker, HiOutlineMail } from "react-icons/hi";
import { IoCardOutline } from "react-icons/io5";
import { MdOutlineShoppingCart } from "react-icons/md";
import { RiFileExcel2Line } from "react-icons/ri";
import { VscFileSymlinkFile } from "react-icons/vsc";
import { useToasts } from "react-toast-notifications";
import { Box, BoxHeading, BoxText, BoxTitle } from "../../../components/Box";
import { CreateAndDetailLayout } from "../../../components/CreateAndDetailLayout";
import { InfoCards } from "../../../components/InfoCards";
import Modal from "../../../components/ModalLib";
import { PanelAndDetailAndCreateHeader } from "../../../components/panel/PanelAndDetailAndCreateHeader";
import { TableContent } from "../../../components/panel/table/TableContent";
import { NOIMAGE_LINK } from "../../../global/variables";
import { OrderItem, useOneOrder } from "../../../hook/queries/useOrdersV2";
import api from "../../../service/api";
import getImageByUrl from "../../../utils/getImageByUrl";
import { groupByObj } from "../../../utils/groupByObj";
import { safeParseNumber } from "../../../utils/safeParseNumber";
import { Tracking } from "../Order/Tracking";
import { Container } from "./styles";

interface Props {
  match: {
    params: {
      id: number;
    };
  };

  admin: boolean;
  client: boolean;
  seller: boolean;
  is_manager?: boolean;
  is_supervisor?: boolean;
}

interface ExportProductsProps {
  noImage?: boolean;
}

export function OrderV2Detail({ match, client }: Props) {
  const getOrder = useOneOrder(match.params.id);
  const order = getOrder?.data;
  const { addToast } = useToasts();

  const [showEmailOptions, setShowEmailOptions] = useState(false);

  const handleDownloadPdf = async () => {
    try {
      addToast?.("Gerando pdf...", {
        appearance: "info",
        autoDismiss: true,
        onDismiss: () => {},
      });

      const response = await api.get(`orderV2/${match.params.id}/pdf`, {
        responseType: "blob",
        headers: {
          Accept: "application/pdf",
        },
      });

      const blob = new Blob([response.data], { type: "application/pdf" });
      const fileURL = URL.createObjectURL(blob);

      window.open(fileURL, "_blank");

      const link = document.createElement("a");
      link.href = fileURL;
      link.download = `pedido-${match.params.id}.pdf`;
      link.click();
    } catch (error) {
      addToast("Não foi possível gerar o PDF do pedido.", {
        appearance: "warning",
        autoDismiss: true,
      });
    }
  };

  const handleDownloadNfe = async () => {
    try {
      if (!order?.code) {
        addToast("Pedido não encontrado ou sem código.", {
          appearance: "warning",
          autoDismiss: true,
        });
        return;
      }

      addToast?.("Baixando NF...", {
        appearance: "info",
        autoDismiss: true,
      });

      const response = await api.post(`/orderV2/${order.id}/invoice`);

      const uri = response?.data?.url;

      if (!uri) {
        addToast("Não foi possível obter o link da NF-e.", {
          appearance: "warning",
          autoDismiss: true,
        });
        return;
      }

      const fileResponse = await api.get(uri, {
        responseType: "blob",
      });

      const blob = new Blob([fileResponse.data], {
        type: "application/zip",
      });

      const href = URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = href;
      link.setAttribute("download", `Nota Fiscal ${order.code}.zip`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(href);
    } catch (error) {
      console.error("Erro ao gerar NF-e:", error);
      addToast(
        "Desculpe, ocorreu um erro interno. Tente novamente mais tarde.",
        {
          appearance: "error",
          autoDismiss: true,
        }
      );
    }
  };

  const handleSendEmail = async (type: "client" | "seller") => {
    try {
      const target =
        type === "client" ? order?.client?.email : order?.seller?.email;

      if (!target) {
        addToast(
          `O e-mail do ${
            type === "client" ? "cliente" : "vendedor"
          } não está disponível.`,
          {
            appearance: "error",
            autoDismiss: true,
          }
        );
        return;
      }

      setShowEmailOptions(false);

      addToast(`Encaminhando pedido...`, {
        appearance: "info",
        autoDismiss: true,
      });

      await api.post(`/orderV2/${order?.id}/send-email`, {
        entity: type,
        email: target,
      });

      addToast(`Pedido foi enviado com sucesso para e-mail ${target}.`, {
        appearance: "success",
        autoDismiss: true,
      });
    } catch (error) {
      addToast(
        "Desculpe, ocorreu um erro interno, Tente novamente mais tarde",
        {
          appearance: "error",
          autoDismiss: true,
        }
      );
    }
  };

  const exportProducts = async ({ noImage = false }: ExportProductsProps) => {
    if (!order)
      return addToast(
        `Não foi possível exportar: pedido não encontrado ou não informado.`,
        {
          appearance: "warning",
          autoDismiss: true,
        }
      );

    addToast(`Exportando dados.`, {
      appearance: "info",
      autoDismiss: true,
    });

    const workbook = new ExcelJS.Workbook();
    const sheet = workbook.addWorksheet("Pedido");
    sheet.getRow(1).height = 25;
    sheet.getRow(1).font = {
      name: "Roboto",
      family: 4,
      size: 16,
      bold: true,
    };

    const columns = [
      { header: "Foto", key: "image", width: 20 },
      { header: "Cód. Pedido", key: "orderCod", width: 25 },
      { header: "Cód. Desdobro", key: "currentOrderCod", width: 25 },
      { header: "Cliente", key: "client", width: 20 },
      { header: "Representante", key: "seller", width: 20 },
      { header: "Tipo", key: "type", width: 20 },
      { header: "Situação", key: "status", width: 20 },
      { header: "Cód. Produto", key: "productCod", width: 25 },
      { header: "Referência", key: "reference", width: 20 },
      { header: "Descrição", key: "description", width: 30 },
      { header: "Cor 1", key: "color1", width: 20 },
      { header: "Cor 2", key: "color2", width: 20 },
      { header: "Marca", key: "brand", width: 10 },
      { header: "Origem", key: "origin", width: 10 },
      { header: "Grade/Tamanho", key: "grid", width: 26 },
      { header: "Preço unitário", key: "value", width: 26 },
      { header: "Desconto", key: "discount", width: 26 },
      { header: "Subtotal", key: "total", width: 26 },
    ];

    //@ts-ignore
    sheet.columns = noImage
      ? columns.filter((f) => f.key !== "image")
      : columns;

    const getImages: { reference: string; imageBase64: string }[] = [];

    if (!noImage) {
      const grouped = groupByObj(order.items || [], (p) => p.product.reference);
      await Promise.all(
        grouped.map(async (group) => {
          const reference = group.value as string;
          const base64 = await getImageByUrl(
            `https://alpar2.sfo3.digitaloceanspaces.com/Produtos/${reference}_01_smaller`
          );
          getImages.push({ reference, imageBase64: base64 });
        })
      );
    }

    const promise = Promise.all(
      (order.items || []).map(async (item, index) => {
        sheet.addRow({
          orderCod: order.code ?? "-",
          currentOrderCod: item.sequence ?? "-",
          type: `${order.species?.name ?? ""} ${moment(
            order.invoiceDate
          ).format("DD/MM/YYYY")}`,
          client: order.client?.company_name
            ? `${order.client.company_name} (${order.client.cod})`
            : "-",
          seller: order.seller?.abbreviation
            ? `${order.seller.abbreviation} (${order.seller.cod})`
            : "-",
          status: item.status?.description ?? "-",
          productCod: item.product.cod ?? "-",
          reference: item.product.reference ?? "-",
          description: item.product.description ?? "-",
          color1: item?.product?.primaryColor ?? "-",
          color2: item?.product?.secondaryColor ?? "-",
          origin: item.product.origin ?? "-",
          brand: order.brand?.name ?? "-",
          grid: item.product.grid ?? "-",
          value: item.netValue ?? 0,
          discount: item.discountValue ?? 0,
          total:
            parseFloat(item.netValue) - parseFloat(item.discountValue || "0"),
        });

        if (!noImage) {
          const imageId = workbook.addImage({
            base64:
              getImages.find((f) => f.reference === item.product.reference)
                ?.imageBase64 ?? "",
            extension: "jpeg",
          });

          sheet.addImage(imageId, {
            tl: { col: 0, row: index + 1 },
            ext: { width: 110, height: 85 },
            editAs: "oneCells",
          });
        }
      })
    );

    await promise;

    sheet.eachRow({ includeEmpty: true }, (row, rowNumber) => {
      row.eachCell({ includeEmpty: true }, (cell) => {
        //@ts-ignore
        let columnKey = cell["_column"]["_key"];
        cell.alignment = {
          vertical: "middle",
          horizontal: "center",
          wrapText: true,
        };

        if (rowNumber !== 1) row.height = 90;

        if (rowNumber === 1) {
          cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "4473c5" },
          };
          cell.font = { color: { argb: "ffffff" } };

          if (["total"].includes(columnKey)) {
            cell.fill = {
              type: "pattern",
              pattern: "solid",
              fgColor: { argb: "22a883" },
            };
          }
        }
      });
    });

    const currentDataFormat = moment().format("DD-MM-YY");

    workbook.xlsx.writeBuffer().then((data) => {
      const blob = new Blob([data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `Pedido ${order.code} (${currentDataFormat}).xlsx`;
      anchor.click();
      window.URL.revokeObjectURL(url);
    });
  };

  const exportOrderGridReport = async () => {
    if (!order) {
      addToast?.("Nenhum pedido selecionado para exportação.", {
        appearance: "warning",
        autoDismiss: true,
      });
      return;
    }

    try {
      addToast?.("Gerando relatório...", {
        appearance: "info",
        autoDismiss: true,
      });

      const workbook = new ExcelJS.Workbook();
      const sheet = workbook.addWorksheet("Relatório por Grade");

      sheet.columns = [
        { header: "Código", key: "codigo", width: 12 },
        { header: "Referência", key: "referencia", width: 16 },
        { header: "Descrição", key: "descricao", width: 40 },
        { header: "Tam.", key: "tamanho", width: 8 },
        { header: "EAN", key: "ean", width: 20 },
        { header: "NCM", key: "ncm", width: 12 },
        { header: "Qnt.", key: "quantidade", width: 8 },
        { header: "U.M.", key: "unidade", width: 8 },
      ];

      const headerRow = sheet.getRow(1);
      headerRow.font = { bold: true };
      headerRow.alignment = { horizontal: "center", vertical: "middle" };
      headerRow.height = 24;

      const items: OrderItem[] = order.items ?? [];

      for (const item of items) {
        const product = item.product;

        if (product.grids && product.grids.length > 0) {
          for (const g of product.grids) {
            const qtd = safeParseNumber(g.qtd);

            sheet.addRow({
              codigo: product.cod,
              referencia: product.reference,
              descricao: product.description,
              tamanho: g.size,
              ean: g.barcode,
              ncm: product.ncm,
              quantidade: qtd,
              unidade: item.stockUnit,
            });
          }
        } else {
          const qtd = item.quantity ?? 0;

          sheet.addRow({
            codigo: product.cod,
            referencia: product.reference,
            descricao: product.description,
            tamanho: "-",
            ean: "-",
            ncm: product.ncm,
            quantidade: qtd,
            unidade: item.stockUnit,
          });
        }
      }
      sheet.eachRow({ includeEmpty: true }, (row, rowNumber) => {
        row.eachCell({ includeEmpty: true }, (cell) => {
          //@ts-ignore
          let columnKey = cell["_column"]["_key"];
          cell.alignment = {
            vertical: "middle",
            horizontal: "center",
            wrapText: true,
          };

          if (rowNumber !== 1) row.height = 90;

          if (rowNumber === 1) {
            cell.fill = {
              type: "pattern",
              pattern: "solid",
              fgColor: { argb: "4473c5" },
            };
            cell.font = { color: { argb: "ffffff" } };

            if (["total"].includes(columnKey)) {
              cell.fill = {
                type: "pattern",
                pattern: "solid",
                fgColor: { argb: "22a883" },
              };
            }
          }
        });
      });

      const now = moment().format("YYYY-MM-DD_HH-mm");
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Relatorio_Pedido_${order.code}_${now}_NCM_EAN.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      addToast?.("Relatório gerado com sucesso.", {
        appearance: "success",
        autoDismiss: true,
      });
    } catch (err) {
      console.error("Erro exportOrderGridReport:", err);
      addToast?.("Erro ao gerar relatório. Tente novamente mais tarde.", {
        appearance: "error",
        autoDismiss: true,
      });
    }
  };

  return (
    <CreateAndDetailLayout isLoading={getOrder?.isLoading}>
      <PanelAndDetailAndCreateHeader
        title={`Pedido ${order?.id}`}
        goBack
        actions={[
          ...(order?.invoiceNumber
            ? [
                {
                  label: "Gerar NF-e",
                  icon: AiOutlineFileSearch,
                  onClick: handleDownloadNfe,
                },
              ]
            : []),
          {
            label: "Espelho do pedido",
            icon: VscFileSymlinkFile,
            onClick: handleDownloadPdf,
          },
          {
            label: "Enviar por e-mail",
            icon: HiOutlineMail,
            onClick: () => setShowEmailOptions(true),
          },
          {
            label: "Exportar pedido",
            icon: RiFileExcel2Line,
            onClick: () => {
              exportProducts({ noImage: true });
            },
          },
          {
            label: "Exportar pedido (COM IMAGEM)",
            icon: RiFileExcel2Line,
            onClick: () => {
              exportProducts({});
            },
          },
          {
            label: "Exportar EAN e NCM",
            icon: RiFileExcel2Line,
            onClick: exportOrderGridReport,
          },
        ]}
      />

      <InfoCards
        style={{
          margin: "0",
        }}
        cards={[
          {
            title: "Código",
            value: order?.code.toString() ?? "-",
          },
          {
            title: "Situação",
            value: order?.status.description ?? "-",
            colorLine: order?.status.color,
          },
          {
            title: "Criado em",
            value: moment(order?.entryDate).format("DD/MM/YYYY"),
          },
          {
            title: "Data de faturamento",
            value: moment(order?.invoiceDate).format("DD/MM/YYYY"),
          },
        ]}
      />

      <Container>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "1.5rem",
            gridArea: "ORDERMAIN",
          }}
        >
          {!!order?.tracking && (
            <Box>
              <BoxHeading>Rastreio</BoxHeading>

              <Tracking
                data={{
                  ...order?.tracking,
                  orderDate: order?.entryDate,
                }}
              />
            </Box>
          )}

          <Box>
            <BoxHeading>Itens do Pedido</BoxHeading>

            <TableContent<OrderItem & { image: string }>
              textAlign="start"
              columns={[
                {
                  field: "Imagem",
                  name: "image",
                  viewList: true,
                  tdStyles: () => ({ padding: "0.5rem" }),
                  render: (item) => (
                    <img
                      src={item.image}
                      alt="Produto"
                      style={{
                        width: "8rem",
                        height: "8rem",
                        objectFit: "contain",
                        borderRadius: "0.5rem",
                      }}
                      onError={({ currentTarget }) => {
                        currentTarget.onerror = null; // prevents looping
                        currentTarget.src = NOIMAGE_LINK;
                      }}
                    />
                  ),
                },
                {
                  field: "Produto",
                  name: "product",
                  viewList: true,
                  render: (item) => (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "start",
                      }}
                    >
                      <strong>{item.product.description}</strong>
                      <span>Referência: {item.product.reference}</span>
                      <span>Grade: {item.product.grid}</span>
                      <span>Origem: {item.product.origin}</span>

                      {!client && (
                        <>
                          {item.canceled?.name && (
                            <span style={{ color: "#d21e26" }}>
                              {item.canceled?.name}
                            </span>
                          )}
                          {item.rejection?.name && (
                            <span style={{ color: "#9700C2" }}>
                              {item.rejection?.name}
                            </span>
                          )}
                        </>
                      )}
                    </div>
                  ),
                },
                {
                  field: "Situação",
                  name: "status",
                  viewList: true,
                  render: (item) => {
                    return (
                      <nav
                        style={{
                          color: item.status.color,
                          fontWeight: "bold",
                        }}
                      >
                        {item.status.description}
                      </nav>
                    );
                  },
                },
                { field: "Qtd", name: "quantity", viewList: true },
                { field: "Valor Unit.", name: "unitValue", viewList: true },
                { field: "Desconto", name: "discountValue", viewList: true },
                { field: "Total", name: "totalValue", viewList: true },
              ]}
              data={order?.items?.map((item) => ({
                ...item,
                image: `https://alpar2.sfo3.digitaloceanspaces.com/Produtos/${item.product.reference}_01_smaller`,
                product: item.product,
                status: item.status,
                quantity: item.quantity,
                netValue: Number(item.netValue).toLocaleString("pt-BR", {
                  style: "currency",
                  currency: "BRL",
                }),
                totalValue: Number(item.netValue).toLocaleString("pt-BR", {
                  style: "currency",
                  currency: "BRL",
                }),
                discountValue: Number(item.discountValue ?? 0).toLocaleString(
                  "pt-BR",
                  {
                    style: "currency",
                    currency: "BRL",
                  }
                ),
                unitValue: Number(
                  Number(item.netValue) / item.quantity
                ).toLocaleString("pt-BR", {
                  style: "currency",
                  currency: "BRL",
                }),
              }))}
            />

            <div style={{ textAlign: "end", marginTop: "1rem" }}>
              <BoxText>
                Subtotal:{" "}
                {Number(order?.totalMerchandise).toLocaleString("pt-BR", {
                  style: "currency",
                  currency: "BRL",
                })}
              </BoxText>
              {/* <BoxText style={{ marginBlock: "0.5rem" }}>
                Desconto: R$ 40,00
              </BoxText> */}
              <BoxHeading style={{ justifyContent: "end" }}>
                Total:{" "}
                {Number(order?.totalInvoiceValue).toLocaleString("pt-BR", {
                  style: "currency",
                  currency: "BRL",
                })}
              </BoxHeading>
            </div>
          </Box>
        </div>

        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "1.5rem",
            gridArea: "ORDERDETAILS",
            width: "100%",
          }}
        >
          {order?.invoiceNumber && (
            <Box>
              <BoxHeading icon={FaFileInvoice}>Numero nota fiscal</BoxHeading>

              <BoxTitle>NF {order?.invoiceNumber}</BoxTitle>
            </Box>
          )}

          <Box>
            <BoxHeading icon={MdOutlineShoppingCart}>Detalhes</BoxHeading>

            <BoxTitle>{order?.brand?.name}</BoxTitle>
            <BoxText style={{ marginBottom: "0.5rem" }}>
              {order?.species.name}
            </BoxText>

            <BoxTitle>Condição pagamento</BoxTitle>
            <BoxText icon={IoCardOutline}>{order?.paymentMethod}</BoxText>
          </Box>

          {!!order?.client && (
            <Box>
              <BoxHeading icon={AiOutlineShop}>Cliente</BoxHeading>
              {!client && (
                <BoxText>{order?.client?.concept?.description}</BoxText>
              )}

              <BoxTitle>
                {order?.client?.trade_name} ({order?.client?.cod})
              </BoxTitle>
              <BoxText style={{ marginBottom: "0.5rem" }}>
                {order?.client?.cnpj}
              </BoxText>
              <BoxText icon={HiOutlineMail}>{order?.client?.email}</BoxText>
              <BoxText icon={FiPhone}>{order?.client?.cellPhone}</BoxText>
              {!!order?.client?.adresses && (
                <BoxText icon={HiOutlineLocationMarker}>
                  {order?.client?.adresses?.street},{" "}
                  {order?.client?.adresses?.numberStreet} -{" "}
                  {order?.client?.adresses?.neighborhood},{" "}
                  {order?.client?.adresses?.city} -{" "}
                  {order?.client?.adresses?.uf},{" "}
                  {order?.client?.adresses?.zip_code}
                </BoxText>
              )}
            </Box>
          )}

          {!!order?.seller && (
            <Box>
              <BoxHeading icon={FiUser}>Representante</BoxHeading>

              <BoxTitle>{order?.seller?.fullName} </BoxTitle>
              <BoxText style={{ marginBottom: "0.5rem" }}>
                {order?.seller?.cod} - {order?.seller?.abbreviation}
              </BoxText>

              <BoxText icon={HiOutlineMail}>{order?.seller?.email}</BoxText>
              <BoxText icon={FiPhone}>{order?.seller?.phone}</BoxText>
            </Box>
          )}
        </div>
      </Container>

      <Modal
        title="Enviar pedido para:"
        modalVisible={showEmailOptions}
        setModalVisible={setShowEmailOptions}
        maxHeight={250}
        maxWidth={650}
      >
        <div
          style={{
            padding: "2rem",
            display: "flex",
            flexDirection: "column",
            gap: "1rem",
            textAlign: "center",
          }}
        >
          <button
            onClick={() => handleSendEmail("client")}
            style={{
              padding: "0.75rem 1rem",
              backgroundColor: "#007bff",
              color: "white",
              border: "none",
              borderRadius: "6px",
              cursor: "pointer",
            }}
          >
            Cliente ({order?.client?.email || "sem email"})
          </button>
          <button
            onClick={() => handleSendEmail("seller")}
            style={{
              padding: "0.75rem 1rem",
              backgroundColor: "#28a745",
              color: "white",
              border: "none",
              borderRadius: "6px",
              cursor: "pointer",
            }}
          >
            Representante ({order?.seller?.email || "sem email"})
          </button>
        </div>
      </Modal>
    </CreateAndDetailLayout>
  );
}
